---
meta:
  - title: Compartir clientes - IUSPRO Docs
  - description: Aprenda a compartir clientes con su equipo y a entender qué puede ver cada persona.
---

# Compartir clientes

**Nota**: Compartir clientes está disponible solo en **planes empresariales**.

Un cliente se puede compartir con otras personas de su organización para que trabajen sobre el mismo contexto.

Esto sirve cuando varias personas necesitan usar:

- las mismas instrucciones del cliente
- los mismos archivos
- los mismos expedientes vinculados
- el mismo espacio general de trabajo

## Qué se comparte exactamente

Cuando comparte un cliente, se comparte el espacio de trabajo del cliente.

Eso incluye:

- nombre y descripción del cliente
- instrucciones del cliente
- archivos adjuntos del cliente
- expedientes vinculados al cliente

No significa que todas las personas vean los chats de todos.

## Regla importante sobre los chats

Aunque un cliente esté compartido, cada usuario ve solo sus propios chats dentro de ese cliente.

Eso permite trabajar sobre el mismo contexto sin mezclar conversaciones privadas entre distintos miembros del equipo.

Piense en la lógica así:

- **el cliente es compartido**
- **los chats siguen siendo personales**

## Tipos de acceso

Al compartir un cliente puede elegir entre:

- **Privado**: solo usted tiene acceso
- **Organización**: todos los miembros activos de su organización pueden usarlo
- **Usuarios específicos**: usted elige exactamente quién entra

## Diferencia entre ver y editar

Cuando comparte con usuarios específicos, puede dar:

- **ver**
- **editar**

### Permiso de ver

Una persona con permiso de **ver** puede:

- abrir el cliente
- usar el contexto del cliente
- crear nuevos chats dentro del cliente
- mover sus propios chats a ese cliente

Pero no puede:

- cambiar el nombre o las instrucciones
- agregar o quitar archivos
- vincular o quitar expedientes
- cambiar la configuración del cliente

### Permiso de editar

Una persona con permiso de **editar** puede hacer todo lo anterior y además:

- modificar el cliente
- agregar archivos
- vincular expedientes
- ajustar instrucciones y configuración

## Cómo compartir un cliente

1. Abra el cliente
2. Busque la opción **Manejar acceso**
3. Elija si será privado, para toda la organización o para usuarios específicos
4. Si escoge usuarios específicos, defina si cada persona tendrá permiso de ver o editar

## Qué pasa con los expedientes vinculados

Si un cliente compartido tiene expedientes adjuntos, el acceso al cliente también habilita el acceso a esos expedientes mientras sigan vinculados a ese cliente.

Esto es importante porque evita tener que compartir por separado cada expediente cuando ya forma parte del trabajo del cliente.

Por ejemplo:

- usted comparte un cliente con otra persona
- dentro del cliente adjunta un expediente
- esa persona podrá usar ese expediente desde el contexto del cliente

Si después quita ese expediente del cliente, ese acceso derivado también deja de aplicar, salvo que el expediente haya sido compartido de forma directa por otro lado.

## Cuándo conviene compartir un cliente

Conviene compartirlo cuando:

- varias personas trabajan el mismo cliente
- quieren usar una misma base de archivos e instrucciones
- desean separar las conversaciones por usuario, pero mantener el mismo contexto general

## Siguiente lectura

- Lea [cómo usar clientes](/guias/proyectos-como-usar)
- Entienda la relación entre [clientes y expedientes](/guias/proyectos-y-expedientes)
- Si va a redactar dentro de un cliente, vea [usar clientes en el redactor](/guias/redactor-usar-proyectos)
